import { useRef, useState } from 'react';

import {
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  Box,
  Button,
  Center,
  Flex,
  HStack,
  IconButton,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Portal,
  Spinner,
  Text,
} from '@chakra-ui/react';

import { ChevronDownIcon, ChevronUpIcon } from '@chakra-ui/icons';

import { Badge, MoreCircleOutline, SavingsCategory } from '@aslanpay/portal-common-lib';

import CategoryFormModal from './CategoryFormModal';
import CategoryImageLightbox from './CategoryImageLightbox';
import CategoryImagesModal from './CategoryImagesModal';
import PresetThumbnail from './PresetThumbnail';
import { LightboxImage, SavingsCategoriesManagerProps } from './types';

// Cap thumbnails shown inline per row; the rest open in the lightbox via a "+N" tile. Keeps the list
// from resolving/downloading every image up front when a category has many.
const MAX_ROW_THUMBNAILS = 5;

/**
 * Presentational manager for savings categories. Renders the category list with publish state and
 * assigned image thumbnails, and — when editing is allowed — create / rename / reorder / delete,
 * publish toggling and image assignment. In company fallback mode it shows the global set read-only
 * with copy / start-from-scratch actions instead.
 */
function SavingsCategoriesManager({
  categories,
  presetImages,
  isLoading,
  isCreating,
  isRenaming,
  isSettingImages,
  isCloning,
  isResetting,
  canEdit,
  isFallback,
  onCreate,
  onRename,
  onDelete,
  onReorder,
  onTogglePublish,
  onSetImages,
  onCloneGlobal,
  onResetToGlobal,
}: SavingsCategoriesManagerProps) {
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [categoryToRename, setCategoryToRename] = useState<SavingsCategory | undefined>(undefined);
  const [categoryForImages, setCategoryForImages] = useState<SavingsCategory | undefined>(
    undefined
  );
  const [categoryToDelete, setCategoryToDelete] = useState<SavingsCategory | undefined>(undefined);
  const [lightbox, setLightbox] = useState<{ images: LightboxImage[]; index: number } | undefined>(
    undefined
  );
  const cancelDeleteRef = useRef<HTMLButtonElement>(null);

  const nameById = new Map(presetImages.map((preset) => [preset.id, preset.name]));

  const moveCategory = (index: number, direction: -1 | 1) => {
    const target = index + direction;
    if (target < 0 || target >= categories.length) {
      return;
    }
    const reordered = [...categories];
    const [moved] = reordered.splice(index, 1);
    reordered.splice(target, 0, moved);
    onReorder(reordered.map((category) => category.id));
  };

  const renderRow = (category: SavingsCategory, index: number) => {
    const orderedImages: LightboxImage[] = [...category.images]
      .sort((first, second) => first.displayOrder - second.displayOrder)
      .map((image) => ({ imageId: image.imageId, fileName: nameById.get(image.imageId) }));

    return (
      <Flex
        key={category.id}
        align="center"
        gap="12px"
        p="12px"
        border="1px solid"
        borderColor="brand.lightGray1"
        borderRadius="10px"
        bg="white"
        data-testid="savings-category-row"
      >
        {canEdit && categories.length > 1 && (
          <Flex direction="column" gap="6px">
            {index > 0 && (
              <IconButton
                aria-label={`Move ${category.name} up`}
                icon={<ChevronUpIcon boxSize="18px" />}
                variant="ghost"
                size="xs"
                onClick={() => moveCategory(index, -1)}
              />
            )}
            {index < categories.length - 1 && (
              <IconButton
                aria-label={`Move ${category.name} down`}
                icon={<ChevronDownIcon boxSize="18px" />}
                variant="ghost"
                size="xs"
                onClick={() => moveCategory(index, 1)}
              />
            )}
          </Flex>
        )}

        <Flex direction="column" gap="8px" flex="1" minW={0}>
          <HStack spacing="10px">
            <Text textStyle="smallSemiBold" color="brand.black" noOfLines={1}>
              {category.name}
            </Text>
            <Badge
              value={category.published ? 'Published' : 'Draft'}
              status={category.published ? 'active' : 'locked'}
            />
          </HStack>
          <HStack spacing="8px">
            {orderedImages.length === 0 ? (
              <Text textStyle="extraSmallRegular" color="brand.gray">
                No images
              </Text>
            ) : (
              <>
                {orderedImages.slice(0, MAX_ROW_THUMBNAILS).map((image, imageIndex) => (
                  <Box
                    key={image.imageId}
                    as="button"
                    type="button"
                    borderRadius="8px"
                    cursor="pointer"
                    aria-label={`View ${image.fileName ?? 'image'}`}
                    onClick={() => setLightbox({ images: orderedImages, index: imageIndex })}
                  >
                    <PresetThumbnail
                      imageId={image.imageId}
                      fileName={image.fileName}
                      size="40px"
                    />
                  </Box>
                ))}
                {orderedImages.length > MAX_ROW_THUMBNAILS && (
                  <Center
                    as="button"
                    type="button"
                    w="40px"
                    h="40px"
                    borderRadius="8px"
                    bg="brand.lightGray2"
                    cursor="pointer"
                    aria-label={`View all ${orderedImages.length} images`}
                    onClick={() =>
                      setLightbox({ images: orderedImages, index: MAX_ROW_THUMBNAILS })
                    }
                  >
                    <Text textStyle="extraSmallMedium" color="brand.darkGray">
                      +{orderedImages.length - MAX_ROW_THUMBNAILS}
                    </Text>
                  </Center>
                )}
              </>
            )}
          </HStack>
        </Flex>

        {canEdit && (
          <HStack spacing="12px">
            <Button
              size="sm"
              variant="outline"
              minW="92px"
              onClick={() => onTogglePublish(category)}
            >
              {category.published ? 'Unpublish' : 'Publish'}
            </Button>
            <Button size="sm" variant="outline" onClick={() => setCategoryForImages(category)}>
              Images
            </Button>
            <Menu autoSelect={false} strategy="fixed">
              <MenuButton
                as={IconButton}
                aria-label={`Actions for ${category.name}`}
                icon={<MoreCircleOutline boxSize="20px" />}
                variant="menu"
              />
              <Portal>
                <MenuList>
                  <MenuItem onClick={() => setCategoryToRename(category)}>Rename</MenuItem>
                  <MenuItem color="brand.danger" onClick={() => setCategoryToDelete(category)}>
                    Delete
                  </MenuItem>
                </MenuList>
              </Portal>
            </Menu>
          </HStack>
        )}
      </Flex>
    );
  };

  const renderContent = () => {
    if (isLoading) {
      return (
        <Center py="40px">
          <Spinner color="brand.purple" size="lg" />
        </Center>
      );
    }

    // In fallback the fetched list is the global set shown only as context in the header — don't
    // render it as this company's categories until the admin copies it in or starts from scratch.
    if (isFallback) {
      return (
        <Center py="40px" border="1px dashed" borderColor="brand.lightGray1" borderRadius="10px">
          <Text textStyle="smallRegular" color="brand.gray">
            This company uses the global categories. Copy them in to customise, or start from
            scratch.
          </Text>
        </Center>
      );
    }

    if (categories.length === 0) {
      return (
        <Center py="40px">
          <Text textStyle="smallRegular" color="brand.gray">
            No savings categories yet.
          </Text>
        </Center>
      );
    }

    return (
      <Flex direction="column" gap="16px">
        {categories.map((category, index) => renderRow(category, index))}
      </Flex>
    );
  };

  return (
    <Flex direction="column" gap="16px" w="full">
      <Flex align="center" justify="space-between" gap="16px" wrap="wrap">
        <Text textStyle="smallRegular" color="brand.gray">
          Categories group preset cover images for employees creating a savings goal.
        </Text>
        <HStack spacing="8px">
          {isFallback ? (
            <>
              <Button variant="outline" isLoading={isCloning} onClick={onCloneGlobal}>
                Copy global set
              </Button>
              <Button onClick={() => setIsCreateOpen(true)}>Start from scratch</Button>
            </>
          ) : (
            canEdit && (
              <>
                {categories.some((category) => category.companyId) && (
                  <Button variant="outline" isLoading={isResetting} onClick={onResetToGlobal}>
                    Reset to global
                  </Button>
                )}
                <Button onClick={() => setIsCreateOpen(true)}>Add category</Button>
              </>
            )
          )}
        </HStack>
      </Flex>

      {renderContent()}

      <CategoryFormModal
        isOpen={isCreateOpen}
        title="Add category"
        isSaving={isCreating}
        onClose={() => setIsCreateOpen(false)}
        onSubmit={(name) => {
          onCreate(name);
          setIsCreateOpen(false);
        }}
      />

      <CategoryFormModal
        isOpen={!!categoryToRename}
        title="Rename category"
        initialName={categoryToRename?.name}
        isSaving={isRenaming}
        onClose={() => setCategoryToRename(undefined)}
        onSubmit={(name) => {
          if (categoryToRename) {
            onRename(categoryToRename.id, name);
          }
          setCategoryToRename(undefined);
        }}
      />

      {categoryForImages && (
        <CategoryImagesModal
          isOpen
          category={categoryForImages}
          presetImages={presetImages}
          isSaving={isSettingImages}
          onClose={() => setCategoryForImages(undefined)}
          onImagesChange={(imageIds) => onSetImages(categoryForImages.id, imageIds)}
        />
      )}

      <AlertDialog
        isOpen={!!categoryToDelete}
        leastDestructiveRef={cancelDeleteRef}
        onClose={() => setCategoryToDelete(undefined)}
      >
        <AlertDialogOverlay>
          <AlertDialogContent data-testid="delete-savings-category-dialog">
            <AlertDialogHeader>Delete category</AlertDialogHeader>
            <AlertDialogBody>
              Are you sure you want to delete “{categoryToDelete?.name}”? This cannot be undone.
            </AlertDialogBody>
            <AlertDialogFooter>
              <Button
                ref={cancelDeleteRef}
                variant="outline"
                onClick={() => setCategoryToDelete(undefined)}
              >
                Cancel
              </Button>
              <Button
                colorScheme="red"
                ml="12px"
                onClick={() => {
                  if (categoryToDelete) {
                    onDelete(categoryToDelete.id);
                  }
                  setCategoryToDelete(undefined);
                }}
              >
                Delete
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>

      {lightbox && (
        <CategoryImageLightbox
          isOpen
          images={lightbox.images}
          startIndex={lightbox.index}
          onClose={() => setLightbox(undefined)}
        />
      )}
    </Flex>
  );
}

export default SavingsCategoriesManager;
